import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  PlusIcon as Plus,
  CaretLeftIcon as CaretLeft,
} from "@phosphor-icons/react";
import {
  fetchAgent,
  listSessions,
  getSession,
  deleteSession,
  listWorkspaces,
  updateSession,
} from "@/lib/api";
import AgentIcon from "@/components/AgentIcon";
import SessionsSidebar from "@/components/chat/SessionsSidebar";
import MessageList from "@/components/chat/MessageList";
import ChatComposer from "@/components/chat/ChatComposer";
import { readFilesToAttachments } from "@/lib/chatFiles";
import { readChatPrefill, prefillNoteText } from "@/lib/chatPrefill";
import { useChatStream } from "@/hooks/useChatStream";

export default function Chat() {
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdParam = searchParams.get("s");
  // Iter 63 — a prepared comparison question (?q=&ws=) is read once; it is never auto-sent.
  const prefillRef = useRef(readChatPrefill(searchParams));
  const [prefill, setPrefill] = useState(null);

  const [agent, setAgent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(sessionIdParam || null);
  const [messages, setMessages] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sending, setSending] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState("");

  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const dragDepth = useRef(0);
  const scrollRef = useRef(null);
  // Suppress the getSession refresh effect when send() just created a session.
  const skipNextLoadRef = useRef(false);

  // ---------- Data loaders ----------
  const loadSessions = useCallback(async () => {
    if (!agentId) return;
    const list = await listSessions({ agent_id: agentId });
    setSessions(list);
  }, [agentId]);

  const loadWorkspaces = useCallback(async () => {
    const ws = await listWorkspaces();
    setWorkspaces(ws);
  }, []);

  useEffect(() => {
    fetchAgent(agentId)
      .then(setAgent)
      .catch(() => navigate("/hub"));
    loadSessions();
    loadWorkspaces();
  }, [agentId, loadSessions, loadWorkspaces, navigate]);

  // Load active session messages
  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      setActiveWorkspaceId(prefillRef.current?.workspaceId || ""); // a pending Iter 63 prefill keeps its workspace
      return;
    }
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    setLoadingSession(true);
    getSession(activeSessionId)
      .then((data) => {
        setMessages(data.messages || []);
        setActiveWorkspaceId(data.session?.workspace_id || "");
        setLoadingSession(false);
      })
      .catch(() => {
        setActiveSessionId(null);
        setLoadingSession(false);
      });
  }, [activeSessionId]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Iter 63 — apply the prepared question (idempotent: StrictMode may run effects twice; the ref is cleared on send / new session).
  useEffect(() => {
    const p = prefillRef.current;
    if (!p || sessionIdParam) return;
    if (p.question) setInput(p.question);
    if (p.workspaceId) setActiveWorkspaceId(p.workspaceId);
    setPrefill(p);
  }, [sessionIdParam]);

  // …and move the caret into the composer once it exists (the agent must be loaded for it to render).
  useEffect(() => {
    if (!prefill || !agent) return;
    const el = document.querySelector('[data-testid="chat-input"]');
    if (!el) return;
    el.focus();
    try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* non-text input */ }
  }, [prefill, agent]);

  // Keep query param in sync with active session id
  useEffect(() => {
    if (activeSessionId) {
      setSearchParams({ s: activeSessionId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [activeSessionId]);

  // ---------- Stream + resume ----------
  const { send, resume } = useChatStream({
    agentId,
    activeSessionId,
    activeWorkspaceId,
    setActiveSessionId,
    setMessages,
    setSending,
    loadSessions,
    skipNextLoadRef,
  });

  // ---------- File ingestion ----------
  const processFiles = useCallback(async (files) => {
    const { attachments: next, skippedType, skippedSize } =
      await readFilesToAttachments(files);
    if (skippedType > 0) {
      toast.error(
        `Skipped ${skippedType} file${
          skippedType === 1 ? "" : "s"
        } — supported: RPG, CL, DDS, SQL, text, JSON, XML, YAML, Markdown.`
      );
    }
    skippedSize.forEach((name) =>
      toast.error(`${name} exceeds 1 MB. Please paste smaller excerpts.`)
    );
    if (next.length > 0) {
      setAttachments((prev) => [...prev, ...next]);
      toast.success(`Attached ${next.length} file${next.length === 1 ? "" : "s"}.`);
    }
  }, []);

  // Drag-and-drop on the composer. Uses a depth counter so dragLeave on
  // child elements doesn't flicker the overlay off.
  const handleDragEnter = useCallback((e) => {
    if (sending) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  }, [sending]);
  const handleDragOver = useCallback((e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (sending) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    await processFiles(files);
  }, [sending, processFiles]);

  const removeAttachment = useCallback((idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // ---------- Sessions ----------
  const handleNewSession = useCallback(() => {
    prefillRef.current = null;
    setActiveSessionId(null);
    setMessages([]);
    setInput("");
    setAttachments([]);
    setPrefill(null);
  }, []);

  const handleSelectSession = useCallback((id) => {
    setActiveSessionId(id);
  }, []);

  const handleDeleteSession = useCallback(async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm("Delete this session?")) return;
    await deleteSession(id);
    if (activeSessionId === id) {
      setActiveSessionId(null);
      setMessages([]);
    }
    await loadSessions();
  }, [activeSessionId, loadSessions]);

  // ---------- Iter 59: ambiguity chip → prepare (never auto-send) the clarified question ----------
  const handlePickAmbiguityChip = useCallback((question) => {
    if (!question) return;
    setInput(question);
    window.requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="chat-input"]');
      if (el) {
        el.focus();
        try { el.setSelectionRange(question.length, question.length); } catch { /* non-text input */ }
      }
    });
  }, []);

  // ---------- Send wrapper that pulls current input + clears optimistic state ----------
  const handleSend = useCallback(async () => {
    const content = input.trim();
    if (!content && attachments.length === 0) return;
    if (sending) return;
    const attsToSend = attachments;
    setInput("");
    setAttachments([]);
    prefillRef.current = null;
    setPrefill(null);
    await send(content, attsToSend, true);
  }, [input, attachments, sending, send]);

  // ---------- Workspace assignment for current session ----------
  const handleAssignWorkspace = useCallback(async (wid) => {
    setActiveWorkspaceId(wid);
    if (activeSessionId) {
      try {
        await updateSession(activeSessionId, { workspace_id: wid || null });
      } catch (e) {
        console.error(e);
      }
      await loadSessions();
    }
  }, [activeSessionId, loadSessions]);

  if (!agent) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center text-[var(--ml-text-secondary)]">
        Loading agent...
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto px-4 lg:px-8 py-6" data-testid="chat-page">
      <ChatHeader
        agent={agent}
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        onAssignWorkspace={handleAssignWorkspace}
        onNewSession={handleNewSession}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 min-h-[70vh]">
        <SessionsSidebar
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelect={handleSelectSession}
          onDelete={handleDeleteSession}
        />

        <section className="lg:col-span-9 flex flex-col ml-card overflow-hidden">
          <MessageList
            scrollRef={scrollRef}
            loadingSession={loadingSession}
            messages={messages}
            agent={agent}
            onResume={resume}
            resuming={sending}
            onSamplePrompt={setInput}
            onPickAmbiguityChip={handlePickAmbiguityChip}
            workspaceId={activeWorkspaceId}
          />
          {prefill && (
            <div
              className="mx-4 lg:mx-5 mb-2 px-3 py-2 border border-[var(--ml-primary)]/40 bg-[var(--ml-primary)]/[0.06] text-xs text-[var(--ml-text-primary)] break-words"
              role="status"
              data-testid="compare-prefill-note"
            >
              {prefillNoteText(workspaces.find((w) => w.id === prefill.workspaceId)?.name)}
            </div>
          )}
          <ChatComposer
            agent={agent}
            input={input}
            setInput={setInput}
            attachments={attachments}
            removeAttachment={removeAttachment}
            sending={sending}
            isDragging={isDragging}
            onSend={handleSend}
            onPickedFiles={processFiles}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        </section>
      </div>
    </div>
  );
}

function ChatHeader({
  agent,
  workspaces,
  activeWorkspaceId,
  onAssignWorkspace,
  onNewSession,
}) {
  return (
    <div className="mb-5 flex items-center gap-4 border-b border-[var(--ml-border)] pb-5">
      <Link to="/hub" className="ml-btn-ghost" data-testid="back-to-hub">
        <CaretLeft size={16} /> Hub
      </Link>
      <div
        className="w-11 h-11 flex items-center justify-center"
        style={{ background: agent.accent }}
      >
        <AgentIcon name={agent.icon} size={22} weight="bold" color="#fff" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {agent.role}
        </div>
        <h1 className="text-lg lg:text-xl font-semibold tracking-tight truncate">
          {agent.name}
        </h1>
      </div>
      <div className="hidden md:flex items-center gap-2">
        <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Workspace
        </label>
        <select
          value={activeWorkspaceId}
          onChange={(e) => onAssignWorkspace(e.target.value)}
          className="ml-input py-1.5 text-sm min-w-[180px]"
          data-testid="chat-workspace-select"
        >
          <option value="">— None —</option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>
      <button
        className="ml-btn-secondary"
        onClick={onNewSession}
        data-testid="new-session-btn"
      >
        <Plus size={14} weight="bold" /> New session
      </button>
    </div>
  );
}
